/**
 * FeedbackBlock
 */

import React, { useState } from 'react';
import { Button, Icon } from 'ui-component-library/base';
import FeedbackForm from 'components/FeedbackForm';
import { ThumbsUpIcon, ThumbsDownIcon } from 'components/Icons';
import { TextHtmlModel } from 'types';

interface Props {
	heading?: string;
	thanksText?: string;
	commentHeading?: string;
	commentNoticeText?: TextHtmlModel;
	apiUrl?: string;
}

const FeedbackBlock: React.FC<Props> = ({
	heading = 'Hjälpte denna sida dig?',
	thanksText = 'Tack för din åsikt!',
	commentHeading = 'Vill du lämna en kommentar?',
	commentNoticeText,
	apiUrl = '/api/sv/feedback/send',
}) => {
	const [showInlineForm, setShowInlineForm] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [successMessage, setSuccessMessage] = useState(false);
	const [isPressed, setIsPressed] = useState(false);
	const [isYesPressed, setIsYesPressed] = useState(false);

	const handleYesClick = async () => {
		if (isPressed) return; // Prevent clicking if already pressed

		setIsPressed(true);
		setIsYesPressed(true);
		setIsLoading(true);
		setError(null);

		// Push event to data layer
		if (window.dataLayer && window.dataLayer.push) {
			window.dataLayer.push({
				event: 'wasThisPageHelpful',
				helpful: 'Yes',
				url: window.location.href,
			});
		}

		try {
			const payload = {
				url: window.location.href,
				helpful: 'Ja',
				comment: '',
			};

			const response = await fetch(apiUrl || '/api/sv/feedback/send', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify(payload),
			});

			if (!response.ok) {
				throw new Error('Failed to submit feedback');
			}

			setSuccessMessage(true);
			setShowInlineForm(false);
		} catch (err) {
			setError(
				err instanceof Error ? err.message : 'Ett fel uppstod vid överföring'
			);
		} finally {
			setIsLoading(false);
		}
	};

	const handleNoClick = () => {
		if (isPressed) return; // Prevent clicking if already pressed

		setIsPressed(true);
		setIsYesPressed(false);
		setShowInlineForm(true);
		setSuccessMessage(false);
		setError(null);

		// Push event to data layer
		if (window.dataLayer && window.dataLayer.push) {
			window.dataLayer.push({
				event: 'wasThisPageHelpful',
				helpful: 'No',
				url: window.location.href,
			});
		}
	};

	const handleFormClose = () => {
		setShowInlineForm(false);
	};

	return (
		<div
			style={{ marginTop: '88px' }}
			className="border-t border-greyLight"
			data-cid="FeedbackBlock"
		>
			{/* Heading and buttons always visible */}
			<div className="pt-8 pb-8 flex items-center justify-between">
				<h2 className="font-alt font-semibold text-h4 m-0 text-black">
					{heading}
				</h2>
				<div className="flex gap-3">
					<Button
						onClick={handleYesClick}
						disabled={isLoading || (isPressed && !isYesPressed)}
						variant="secondary"
						className={`flex items-center gap-2 justify-between transition-opacity ${
							isPressed && isYesPressed
								? 'ring-2 ring-blue-500 cursor-not-allowed'
								: 'hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed'
						}`}
						aria-label="Ja, detta var hjälpsamt"
					>
						<ThumbsUpIcon width={16} height={16} />
						<span>Ja</span>
					</Button>
					<Button
						onClick={handleNoClick}
						disabled={isLoading || (isPressed && isYesPressed)}
						variant="secondary"
						className={`flex items-center gap-2 justify-between transition-opacity ${
							isPressed && !isYesPressed
								? 'ring-2 ring-blue-500 cursor-not-allowed'
								: 'hover:opacity-80 disabled:opacity-50 disabled:cursor-not-allowed'
						}`}
						aria-label="Nej, detta var inte hjälpsamt"
					>
						<ThumbsDownIcon width={16} height={16} />
						<span>Nej</span>
					</Button>
				</div>
			</div>

			{/* Content appears below heading and buttons */}
			{isLoading && (
				<div className="flex items-center justify-center mt-2">
					<Icon
						icon="loader"
						size={3}
						animate="spin"
						color="#1B7765"
						aria-label="Laddar"
					/>
				</div>
			)}

			{error && (
				<div className="mt-2">
					<p className="text-red text-p mb-4">{error}</p>
				</div>
			)}

			{successMessage && !showInlineForm && (
				<div className="mt-2">
					<p className="font-alt font-normal text-p text-black mb-4">
						{thanksText}
					</p>
				</div>
			)}

			{showInlineForm && (
				<div className="mt-2">
					<FeedbackForm
						commentHeading={commentHeading}
						commentNoticeText={commentNoticeText}
						apiUrl={apiUrl}
						initialSelection="Nej"
						onClose={handleFormClose}
					/>
				</div>
			)}
		</div>
	);
};

export default FeedbackBlock;
